import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Plus, Edit2, Trash2, Loader2, X, CheckCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface GiftCatalogItem {
  id: number;
  key: string;
  name: string;
  description?: string;
  isActive: number | boolean;
  createdAt?: number;
  updatedAt?: number;
}

function toBool(v: number | boolean | undefined): boolean {
  return v === 1 || v === true;
}

function formatDateTime(ts?: number): string {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString('zh-HK');
}

const KEY_PATTERN = /^[a-z0-9_-]+$/;

const defaultForm = {
  key: '',
  name: '',
  description: '',
  isActive: true,
};

const AdminGiftCatalog: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const [gifts, setGifts] = useState<GiftCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadGifts();
  }, [token, navigate]);

  const handleUnauthorized = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const loadGifts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '獲取禮品目錄失敗');
      setGifts(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取禮品目錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (gift: GiftCatalogItem) => {
    setEditingId(gift.id);
    setForm({
      key: gift.key || '',
      name: gift.name || '',
      description: gift.description || '',
      isActive: toBool(gift.isActive),
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.name.trim()) {
      setFormError('請填寫禮品名稱');
      return;
    }
    if (!editingId) {
      if (!form.key.trim()) {
        setFormError('請填寫禮品 Key');
        return;
      }
      if (!KEY_PATTERN.test(form.key.trim())) {
        setFormError('Key 只允許小寫英文字母、數字、底線（_）及連字號（-）');
        return;
      }
    }

    setSaving(true);
    try {
      const url = editingId
        ? `/api/public/admin/gift-catalog/${editingId}`
        : '/api/public/admin/gift-catalog';
      const method = editingId ? 'PUT' : 'POST';
      const payload = editingId
        ? {
            name: form.name.trim(),
            description: form.description.trim(),
            isActive: form.isActive,
          }
        : {
            key: form.key.trim(),
            name: form.name.trim(),
            description: form.description.trim(),
            isActive: form.isActive,
          };
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess(editingId ? '禮品已更新' : '禮品已建立');
      closeModal();
      loadGifts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setFormError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (gift: GiftCatalogItem) => {
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/gift-catalog/${gift.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: gift.name,
          description: gift.description || '',
          isActive: !toBool(gift.isActive),
        }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '更新狀態失敗');
      setSuccess(toBool(gift.isActive) ? '禮品已停用' : '禮品已啟用');
      loadGifts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || '更新狀態失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這個禮品嗎？')) return;
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/gift-catalog/${id}`, { method: 'DELETE' });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      setSuccess('禮品已刪除');
      loadGifts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    }
  };

  return (
    <AdminLayout currentPage="gift-catalog" onLogout={handleUnauthorized}>
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Gift className="w-7 h-7 text-orange-600" />
            禮品目錄
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            管理可透過客戶旅程（Workflow）贈送的隨單禮品。禮品會掛在客戶名下，並在下一次落單時自動附送。
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
        >
          <Plus size={20} /> 新增禮品
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4">Key</th>
                <th className="p-4">名稱</th>
                <th className="p-4">說明</th>
                <th className="p-4">狀態</th>
                <th className="p-4">建立時間</th>
                <th className="p-4 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> 加載中...
                  </td>
                </tr>
              ) : gifts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    暫無禮品，點擊右上角「新增禮品」開始建立
                  </td>
                </tr>
              ) : (
                gifts.map((gift) => (
                  <tr key={gift.id} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4 font-mono text-xs text-gray-600 dark:text-gray-300">{gift.key}</td>
                    <td className="p-4 text-gray-800 dark:text-white font-medium">{gift.name}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-400 max-w-sm truncate" title={gift.description || ''}>
                      {gift.description || '-'}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => handleToggleActive(gift)}
                        title="點擊切換啟用 / 停用"
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                          toBool(gift.isActive)
                            ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/40'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {toBool(gift.isActive) ? '啟用' : '停用'}
                      </button>
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDateTime(gift.createdAt)}</td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => openEdit(gift)}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                      >
                        <Edit2 size={14} /> 編輯
                      </button>
                      <button
                        onClick={() => handleDelete(gift.id)}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs"
                      >
                        <Trash2 size={14} /> 刪除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingId ? '編輯禮品' : '新增禮品'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  disabled={!!editingId}
                  value={form.key}
                  onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                  placeholder="例如：free-soup"
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none font-mono text-sm ${
                    editingId
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-white'
                  }`}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  客戶旅程會用此 Key 引用禮品，建立後不可修改
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="例如：免費老火湯一盅"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">說明</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="禮品內容、兌換方式等備註（可選）"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="gift-is-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
                <label htmlFor="gift-is-active" className="text-sm font-medium text-gray-700 dark:text-gray-300">啟用</label>
              </div>

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white dark:bg-gray-800 pb-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? '儲存變更' : '建立禮品'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminGiftCatalog;
